export default class GooglePay {

    constructor (tokenizationSpecification, allowedCardNetworks, allowedCardAuthMethods) {
        const self = this;

        this.event = {
            'PAYMENT_PROCESSED': 'PAYMENT_PROCESSED'
        };
        this.eventListeners = {};
        /**
         * Define the version of the Google Pay API referenced when creating your
         * configuration
         *
         * @see {@link https://developers.google.com/pay/api/web/reference/object#PaymentDataRequest|apiVersion in PaymentDataRequest}
         */
        this.baseRequest = {
            apiVersion: 2,
            apiVersionMinor: 0
        };

        /**
         * Card networks supported by your site and your gateway
         *
         * @see {@link https://developers.google.com/pay/api/web/reference/object#CardParameters|CardParameters}
         * @todo confirm card networks supported by your site and gateway
         */
        this.allowedCardNetworks = allowedCardNetworks ? allowedCardNetworks : ["AMEX", "DISCOVER", "INTERAC", "JCB", "MASTERCARD", "VISA"];

        /**
         * Card authentication methods supported by your site and your gateway
         *
         * @see {@link https://developers.google.com/pay/api/web/reference/object#CardParameters|CardParameters}
         * @todo confirm your processor supports Android device tokens for your
         * supported card networks
         */
        this.allowedCardAuthMethods = allowedCardAuthMethods ? allowedCardAuthMethods : ["PAN_ONLY", "CRYPTOGRAM_3DS"];

        /**
         * Identify your gateway and your site's gateway merchant identifier
         *
         * The Google Pay API response will return an encrypted payment method capable
         * of being charged by a supported gateway after payer authorization
         *
         * @todo check with your gateway on the parameters to pass
         * @see {@link https://developers.google.com/pay/api/web/reference/object#Gateway|PaymentMethodTokenizationSpecification}
         */
        this.tokenizationSpecification = tokenizationSpecification ? tokenizationSpecification : {
                type: 'PAYMENT_GATEWAY',
                parameters: {
                    'gateway': 'example',
                    'gatewayMerchantId': 'exampleGatewayMerchantId'
                }
            };

        /**
         * Describe your site's support for the CARD payment method and its required
         * fields
         *
         * @see {@link https://developers.google.com/pay/api/web/reference/object#CardParameters|CardParameters}
         */
         this.baseCardPaymentMethod = {
            type: 'CARD',
            parameters: {
                allowedAuthMethods: this.allowedCardAuthMethods,
                allowedCardNetworks: this.allowedCardNetworks
            }
        };

        /**
         * Describe your site's support for the CARD payment method including optional
         * fields
         *
         * @see {@link https://developers.google.com/pay/api/web/reference/object#CardParameters|CardParameters}
         */
         this.cardPaymentMethod = Object.assign(
            {},
            this.baseCardPaymentMethod,
            {
                tokenizationSpecification: this.tokenizationSpecification
            }
        );

        /**
         * An initialized google.payments.api.PaymentsClient object or null if not yet set
         *
         * @see {@link getGooglePaymentsClient}
         */
        this.paymentsClient = null;

        this.paymentToken = null;

        this.container = 'gpay-container';

        let googlePayScriptId = 'google-pay-integration';
        let script = document.getElementById(googlePayScriptId);
        if (!script) {
            let script = document.createElement('script');
            script.onload = () => {
                self.onGooglePayLoaded()
            };
            script.src = 'https://pay.google.com/gp/p/js/pay.js';
            script.id = googlePayScriptId;
            document.getElementsByTagName('head')[0].appendChild(script);
        }
    }

    /**
     * Configure your site's support for payment methods supported by the Google Pay
     * API.
     *
     * Each member of allowedPaymentMethods should contain only the required fields,
     * allowing reuse of this base request when determining a viewer's ability
     * to pay and later requesting a supported payment method
     *
     * @returns {object} Google Pay API version, payment methods supported by the site
     */
    getGoogleIsReadyToPayRequest () {
        const self = this;
        return Object.assign(
            {},
            self.baseRequest,
            {
                allowedPaymentMethods: [self.baseCardPaymentMethod]
            }
        );
    }

    /**
     * Configure support for the Google Pay API
     *
     * @see {@link https://developers.google.com/pay/api/web/reference/object#PaymentDataRequest|PaymentDataRequest}
     * @returns {object} PaymentDataRequest fields
     */
    getGooglePaymentDataRequest() {
        const paymentDataRequest = Object.assign({}, this.baseRequest);
        paymentDataRequest.allowedPaymentMethods = [this.cardPaymentMethod];
        paymentDataRequest.transactionInfo = this.getGoogleTransactionInfo();
        paymentDataRequest.merchantInfo = {
            // @todo a merchant ID is available for a production environment after approval by Google
            // See {@link https://developers.google.com/pay/api/web/guides/test-and-deploy/integration-checklist|Integration checklist}
            // merchantId: '01234567890123456789',
            merchantName: 'Example Merchant'
        };

        return paymentDataRequest;
    }

    /**
     * Return an active PaymentsClient or initialize
     *
     * @see {@link https://developers.google.com/pay/api/web/reference/client#PaymentsClient|PaymentsClient constructor}
     * @returns {google.payments.api.PaymentsClient} Google Pay API client
     */
    getGooglePaymentsClient() {
        const self = this;
        if ( this.paymentsClient === null ) {
            let options = {
                environment: "TEST",
                merchantInfo: {
                    merchantName: "Example Merchant",
                    merchantId: "0123456789"
                },
                // paymentDataCallbacks: {
                //     onPaymentDataChanged: (data) => {
                //         self.onPaymentDataChanged(data);
                //     },
                //     onPaymentAuthorized: (paymentData) => {
                //         self.onPaymentAuthorized(paymentData);
                //     }
                // }
            };

            this.paymentsClient = new google.payments.api.PaymentsClient(options);
        }
        return this.paymentsClient;
    }

    /**
     * Initialize Google PaymentsClient after Google-hosted JavaScript has loaded
     *
     * Display a Google Pay payment button after confirmation of the viewer's
     * ability to pay.
     */
    onGooglePayLoaded() {
        const paymentsClient = this.getGooglePaymentsClient();
        const self = this;

        paymentsClient.isReadyToPay(this.getGoogleIsReadyToPayRequest())
        .then(function(response) {
            if (response.result) {
                self.addGooglePayButton();
                // @todo prefetch payment data to improve performance after confirming site functionality
                self.prefetchGooglePaymentData();
            }
        })
        .catch(function(err) {
            // show error in developer console for debugging
            console.error(err);
        });
    }

    setContainer (id) {
        if (id) {
            this.container = id;
        }
    }

    /**
     * Add a Google Pay purchase button alongside an existing checkout button
     *
     * @see {@link https://developers.google.com/pay/api/web/reference/object#ButtonOptions|Button options}
     * @see {@link https://developers.google.com/pay/api/web/guides/brand-guidelines|Google Pay brand guidelines}
     */
    addGooglePayButton() {
        const paymentsClient = this.getGooglePaymentsClient();
        const self = this;
        const button =
            paymentsClient.createButton({onClick: () => {
                    self.onGooglePaymentButtonClicked();
                }});
        document.getElementById(this.container).appendChild(button);
    }

    /**
     * Provide Google Pay API with a payment amount, currency, and amount status
     *
     * @see {@link https://developers.google.com/pay/api/web/reference/object#TransactionInfo|TransactionInfo}
     * @returns {object} transaction info, suitable for use as transactionInfo property of PaymentDataRequest
     */
    getGoogleTransactionInfo() {
        return {
            countryCode: 'US',
            currencyCode: 'USD',
            totalPriceStatus: 'FINAL',
            // set to cart total
            totalPrice: '1.00'
        };
    }

    setGoogleTransactionInfo(totalPriceStatus, totalPrice, currencyCode, countryCode) {
        this.transactionInfo = {};
        this.transactionInfo.totalPriceStatus = totalPriceStatus;
        this.transactionInfo.totalPrice = totalPrice;
        this.transactionInfo.currencyCode = currencyCode;
        this.transactionInfo.countryCode = countryCode;
    }

    /**
     * Prefetch payment data to improve performance
     *
     * @see {@link https://developers.google.com/pay/api/web/reference/client#prefetchPaymentData|prefetchPaymentData()}
     */
    prefetchGooglePaymentData() {
        const paymentDataRequest = this.getGooglePaymentDataRequest();
        // transactionInfo must be set but does not affect cache
        paymentDataRequest.transactionInfo = {
            totalPriceStatus: 'NOT_CURRENTLY_KNOWN',
            currencyCode: 'USD'
        };
        const paymentsClient = this.getGooglePaymentsClient();
        paymentsClient.prefetchPaymentData(paymentDataRequest);
    }

    /**
     * Show Google Pay payment sheet when Google Pay payment button is clicked
     */
    onGooglePaymentButtonClicked() {
        const paymentDataRequest = this.getGooglePaymentDataRequest();
        paymentDataRequest.transactionInfo = this.getGoogleTransactionInfo();

        const paymentsClient = this.getGooglePaymentsClient();
        const self = this;
        paymentsClient.loadPaymentData(paymentDataRequest)
            .then(function(paymentData) {
                // handle the response
                self.processPayment(paymentData);
            })
            .catch(function(err) {
                // show error in developer console for debugging
                console.error(err);
            });
    }

    /**
     * Process payment data returned by the Google Pay API
     *
     * @param {object} paymentData response from Google Pay API after user approves payment
     * @see {@link https://developers.google.com/pay/api/web/reference/object#PaymentData|PaymentData object reference}
     */
    processPayment(paymentData) {
        // show returned data in developer console for debugging
        this.triggerEvent(this.event.PAYMENT_PROCESSED, paymentData);
        // @todo pass payment token to your gateway to process payment
        this.paymentToken = paymentData.paymentMethodData.tokenizationData.token;
    }

    registerEvent(event, handler) {
        this.eventListeners[event] = handler;
    }

    triggerEvent(event, payload){
        if (typeof this.eventListeners[event] === 'function') {
            this.eventListeners[event](payload);
        }
    }

    onPaymentDataChanged(info) {
        // console.log(info);
    }

    onPaymentAuthorized(paymentData) {
        // console.log(paymentData);
        return new Promise((resolve, reject) => {
            // handle the response
            processPayment(paymentData)
                .then(() => {
                    resolve({transactionState: 'SUCCESS'});
                })
                .catch(() => {
                    resolve({
                        transactionState: 'ERROR',
                        error: {
                            intent: 'PAYMENT_AUTHORIZATION',
                            message: 'Insufficient funds',
                            reason: 'PAYMENT_DATA_INVALID'
                        }
                    });
                });
        });
    }
}


// WEBPACK FOOTER //
// ./src/app/lib/GooglePay.js